import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowUpCircle, Calendar, CheckCircle,
  Clock, IndianRupee, Wallet,
} from 'lucide-react';
import { withdrawalAPI } from '../api/withdrawal';
import { investmentAPI } from '../api/investment';
import { Card } from '../components/ui/Card';
import Button from '../components/ui/Button';
import StatusBadge from '../components/ui/StatusBadge';
import PageHeader from '../components/ui/PageHeader';

const PERIODS = [
  { value: '3_months', label: '3 Months', desc: 'Quarterly' },
  { value: '6_months', label: '6 Months', desc: 'Half-yearly' },
  { value: '9_months', label: '9 Months', desc: 'Long-term' },
];

const periodLabel = (v) => PERIODS.find((p) => p.value === v)?.label ?? v;
const fmt = (n) => Number(n).toLocaleString('en-IN');

const CHIP_INACTIVE = { background: 'rgba(212,160,23,0.1)', color: '#f0c040', border: '1px solid rgba(212,160,23,0.3)' };
const CHIP_ACTIVE   = { background: 'linear-gradient(135deg, #d4a017, #f0c040)', color: '#fff', boxShadow: '0 2px 10px rgba(212,160,23,0.35)' };
const TAB_INACTIVE  = { color: 'rgba(255,255,255,0.5)' };
const TAB_ACTIVE    = { background: 'linear-gradient(135deg, #d4a017, #f0c040)', color: '#fff', boxShadow: '0 2px 10px rgba(212,160,23,0.3)' };

export default function WithdrawPage() {
  const [tab, setTab] = useState('request');

  const [amount, setAmount]           = useState('');
  const [period, setPeriod]           = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [lastRequest, setLastRequest] = useState(null);
  const [apiError, setApiError]       = useState('');

  const [history, setHistory]         = useState([]);
  const [pagination, setPagination]   = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [loadingH, setLoadingH]       = useState(false);

  const [balance, setBalance] = useState(null);

  useEffect(() => {
    investmentAPI.getBalance()
      .then((r) => setBalance(r.data.data))
      .catch(() => {});
  }, []);

  const loadHistory = useCallback((page = 1) => {
    setLoadingH(true);
    withdrawalAPI.getHistory({ page, per_page: 10 })
      .then((r) => {
        setHistory(r.data.data.withdrawals   ?? []);
        setPagination(r.data.data.pagination ?? null);
        setHistoryPage(page);
      })
      .catch(() => {})
      .finally(() => setLoadingH(false));
  }, []);

  useEffect(() => {
    if (tab === 'history') loadHistory(1);
  }, [tab, loadHistory]);

  const availableBalance = parseFloat(balance?.total_balance ?? 0);
  const amountNum        = parseFloat(amount) || 0;
  const exceedsBalance   = amountNum > 0 && amountNum > availableBalance;

  const handleSubmit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < 1) {
      setApiError('Please enter a valid amount.');
      return;
    }
    if (amt > availableBalance) {
      setApiError(`Amount cannot exceed your available balance of ₹${fmt(availableBalance)}.`);
      return;
    }
    if (!period) {
      setApiError('Please select a withdrawal period.');
      return;
    }
    setApiError('');
    setSubmitting(true);
    try {
      const res = await withdrawalAPI.requestWithdrawal({
        amount:            amt,
        withdrawal_period: period,
      });
      setLastRequest(res.data.data);
      setAmount('');
      setPeriod('');
    } catch (err) {
      const errs = err.response?.data?.errors;
      if (errs) {
        const first = Object.values(errs)[0]?.[0];
        setApiError(first || 'Validation failed.');
      } else {
        setApiError(err.response?.data?.message || 'Request failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader title="Withdraw" subtitle="Transfer your earnings" />

      {/* Balance card */}
      {balance && (
        <div className="mx-4 mb-4">
          <div
            className="rounded-3xl px-5 py-4 flex items-center gap-4"
            style={{ background: 'linear-gradient(135deg, #d4a017 0%, #f0c040 100%)' }}
          >
            <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Wallet size={20} color="white" />
            </div>
            <div>
              <p className="text-xs text-amber-100 font-medium">Available Balance</p>
              <p className="text-2xl font-serif font-bold text-white">
                ₹{fmt(balance.total_balance)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div
        className="flex gap-1 mx-4 mb-4 rounded-2xl p-1"
        style={{ background: 'rgba(255,255,255,0.08)' }}
      >
        {[
          { key: 'request', label: 'New Request' },
          { key: 'history', label: 'History' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => { setTab(key); setLastRequest(null); }}
            className="flex-1 py-2.5 text-sm font-semibold rounded-xl transition-all"
            style={tab === key ? TAB_ACTIVE : TAB_INACTIVE}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="px-4 pb-6">

        {/* ── Request Tab ────────────────────────────── */}
        {tab === 'request' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col gap-4"
          >
            <AnimatePresence>
              {lastRequest && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Card>
                    <div className="flex flex-col items-center gap-3 py-2">
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center"
                        style={{ background: 'rgba(5,150,105,0.18)' }}
                      >
                        <CheckCircle size={28} color="#6ee7b7" />
                      </div>
                      <div className="text-center">
                        <p className="font-serif font-bold text-white text-lg">
                          Request Submitted!
                        </p>
                        <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.55)' }}>
                          ₹{fmt(lastRequest.amount)} · {periodLabel(lastRequest.withdrawal_period)}
                        </p>
                        <div className="mt-1">
                          <StatusBadge status={lastRequest.status} />
                        </div>
                        <p className="text-xs mt-2" style={{ color: 'rgba(255,255,255,0.4)' }}>
                          Your withdrawal is under review.
                        </p>
                      </div>
                      <button
                        onClick={() => setLastRequest(null)}
                        className="text-xs underline underline-offset-2"
                        style={{ color: '#f0c040' }}
                      >
                        Make another request
                      </button>
                    </div>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>

            {!lastRequest && (
              <Card>
                <div className="flex items-center gap-2 mb-5">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #d4a017, #f0c040)' }}
                  >
                    <ArrowUpCircle size={16} color="white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">Withdrawal Request</p>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>Enter amount and select period</p>
                  </div>
                </div>

                {apiError && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mb-4 rounded-xl border px-4 py-3 text-sm"
                    style={{ background: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.3)', color: '#fca5a5' }}
                  >
                    {apiError}
                  </motion.div>
                )}

                {/* Amount input */}
                <div className="flex flex-col gap-1 mb-4">
                  <div className="flex items-center justify-between">
                    <label className="input-label flex items-center gap-1.5">
                      <IndianRupee size={12} color="#f0c040" />
                      Withdrawal Amount
                    </label>
                    {balance && availableBalance > 0 && (
                      <button
                        type="button"
                        onClick={() => { setAmount(availableBalance.toFixed(2)); setApiError(''); }}
                        className="text-[11px] font-semibold px-2 py-0.5 rounded-lg transition-all active:scale-95"
                        style={{ background: 'rgba(212,160,23,0.15)', color: '#f0c040', border: '1px solid rgba(212,160,23,0.3)' }}
                      >
                        Max
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: '#f0c040' }}>
                      <IndianRupee size={15} />
                    </span>
                    <input
                      type="number"
                      min="1"
                      max={availableBalance || undefined}
                      placeholder="Enter amount"
                      value={amount}
                      onChange={(e) => { setAmount(e.target.value); setApiError(''); }}
                      className="input-field pl-9 w-full"
                      style={exceedsBalance ? { borderColor: 'rgba(239,68,68,0.6)' } : {}}
                    />
                  </div>
                  {exceedsBalance && (
                    <motion.p
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-xs mt-0.5"
                      style={{ color: '#fca5a5' }}
                    >
                      Exceeds available balance of ₹{fmt(availableBalance)}
                    </motion.p>
                  )}
                </div>

                {/* Period chips */}
                <div className="flex flex-col gap-1 mb-4">
                  <label className="input-label flex items-center gap-1.5">
                    <Clock size={12} color="#f0c040" />
                    Withdrawal Period
                  </label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {PERIODS.map((p) => (
                      <motion.button
                        key={p.value}
                        type="button"
                        whileTap={{ scale: 0.94 }}
                        onClick={() => { setPeriod(p.value); setApiError(''); }}
                        className="rounded-2xl py-3 px-2 text-center transition-all"
                        style={period === p.value ? CHIP_ACTIVE : CHIP_INACTIVE}
                      >
                        <p className="text-sm font-bold leading-tight">{p.label}</p>
                        <p className={`text-[10px] mt-0.5 ${period === p.value ? 'text-amber-100' : ''}`}
                           style={period !== p.value ? { color: 'rgba(240,192,64,0.6)' } : {}}>
                          {p.desc}
                        </p>
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Summary row */}
                {amount && period && parseFloat(amount) > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between rounded-2xl border px-4 py-3 mb-4"
                    style={{ background: 'rgba(212,160,23,0.1)', borderColor: 'rgba(212,160,23,0.3)' }}
                  >
                    <div>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>Withdrawing</p>
                      <p className="text-base font-bold" style={{ color: '#f0c040' }}>
                        ₹{fmt(parseFloat(amount))}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>Period</p>
                      <p className="text-sm font-semibold text-white">
                        {periodLabel(period)}
                      </p>
                    </div>
                  </motion.div>
                )}

                <Button
                  fullWidth
                  loading={submitting}
                  disabled={!amount || !period || parseFloat(amount) < 1 || exceedsBalance}
                  onClick={handleSubmit}
                >
                  Submit Request
                </Button>
              </Card>
            )}
          </motion.div>
        )}

        {/* ── History Tab ────────────────────────────── */}
        {tab === 'history' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col gap-3"
          >
            {loadingH ? (
              [1, 2, 3].map((i) => (
                <div key={i} className="skeleton h-16 rounded-2xl" />
              ))
            ) : history.length === 0 ? (
              <Card className="text-center py-10">
                <ArrowUpCircle size={32} className="mx-auto mb-2" style={{ color: 'rgba(255,255,255,0.2)' }} />
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>No withdrawal history yet</p>
              </Card>
            ) : (
              <>
                {history.map((w, idx) => (
                  <motion.div
                    key={w.id || idx}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Card className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(5,150,105,0.18)' }}
                      >
                        <ArrowUpCircle size={18} color="#6ee7b7" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white">
                          {periodLabel(w.withdrawal_period)}
                        </p>
                        <p className="text-xs flex items-center gap-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
                          <Calendar size={11} />
                          {w.requested_at
                            ? new Date(w.requested_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                            : '—'}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-white">
                          ₹{fmt(w.amount)}
                        </p>
                        <StatusBadge status={w.status} />
                      </div>
                    </Card>
                  </motion.div>
                ))}

                {pagination && pagination.last_page > 1 && (
                  <div className="flex items-center justify-between pt-2">
                    <button
                      disabled={historyPage === 1}
                      onClick={() => loadHistory(historyPage - 1)}
                      className="text-xs font-semibold px-3 py-2"
                      style={{ color: historyPage === 1 ? 'rgba(255,255,255,0.2)' : '#f0c040' }}
                    >
                      ← Previous
                    </button>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      Page {historyPage} of {pagination.last_page}
                    </p>
                    <button
                      disabled={historyPage === pagination.last_page}
                      onClick={() => loadHistory(historyPage + 1)}
                      className="text-xs font-semibold px-3 py-2"
                      style={{ color: historyPage === pagination.last_page ? 'rgba(255,255,255,0.2)' : '#f0c040' }}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}

      </div>
    </div>
  );
}
